import { useCallback } from 'react'

import { useClose } from '@headlessui/react'
import { formatDistanceToNow } from 'date-fns'

import OpenNewTabIcon from '@/assets/icons/icon-open-new-tab.svg'
import { Button } from '@/components/ui/button'
import { useOpenChat } from '@/modules/chatting/hooks/use-open-chat'
import { useToggleFollow } from '@/modules/chatting/hooks/use-toggle-follow'
import ArrowInCircleIcon from '@/modules/chatting/icons/arrow-circle.svg'
import ChatIcon from '@/modules/chatting/icons/chat.svg'
import PinIcon from '@/modules/chatting/icons/pin.svg'
import VerifiedIcon from '@/modules/chatting/icons/verified.svg'
import { useChattingTabs } from '@/modules/chatting/state/tabs-provider.ts'
import type { ListUser } from '@/modules/chatting/types'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { classNames } from '@/utils/helpers'

import { useCollectionContext } from '../../../CollectionProvider'
import { ScrollingUserLists } from '../../../components/ScrollingUserLists'
import { MoreUserActions } from './UserActionsDropdown'

interface Props {
  user: ListUser
  onClickPin: () => void
  isPinLoading: boolean
}

export const UnsubscribedUserCard = ({
  user,
  onClickPin,
  isPinLoading,
}: Props) => {
  const closeCollectionsDialog = useClose()
  const { isChatOpening, handleOpenChat } = useOpenChat()
  const { collection, updateUserInCache } = useCollectionContext()
  const { openNewTab, isTabsLimitReached } = useChattingTabs()

  const onToggleFollow = (subscribedBy: boolean) => {
    updateUserInCache({ ...user, subscribedBy })
  }
  const { followMutation, unfollowMutation } = useToggleFollow(onToggleFollow)

  const toggleFollow = () => {
    if (user.subscribedBy) {
      unfollowMutation.mutate(user.id)
    } else {
      followMutation.mutate(user.id)
    }
  }

  const handleClickMessage = async () => {
    await handleOpenChat(user.id)
    closeCollectionsDialog()
  }

  const handleOpenNewTab = useCallback(() => {
    const { id, name, avatar, displayName, username, avatarThumbs } = user

    openNewTab({
      fanId: id,
      // TODO: IMPLEMENT SOLUTION FOR INBOX SPLITTER \ FAN NUMBERING
      // fanNumber,
      avatar: avatarThumbs?.c50 ?? avatarThumbs?.c144 ?? avatar,
      title: displayName || name || username,
    })
  }, [openNewTab, user])

  return (
    <div className="bg-secondary-50 dark:bg-secondary-600 rounded-lg">
      <div className="bg-primary-50 dark:bg-secondary-500 flex h-[105px] flex-col gap-4 rounded-t-lg p-3">
        <div className="flex items-center justify-end gap-3">
          <IconButton
            icon={OpenNewTabIcon}
            iconClassName="size-[24px] thin-stroke"
            className="text-secondary-300"
            tooltip={
              isTabsLimitReached
                ? 'You have reached your maximum tab limit'
                : 'Open Chat in New Tab'
            }
            onClick={handleOpenNewTab}
          />
          <IconButton
            icon={ArrowInCircleIcon}
            className="text-secondary-300 rotate-90"
            tooltip="Open OnlyFans profile"
            onClick={() => {
              window.open(`https://onlyfans.com/${user.username}`, '_blank')
            }}
          />
          {collection?.type === 'custom' && (
            <IconButton
              icon={PinIcon}
              onClick={onClickPin}
              disabled={isPinLoading}
              className={classNames('text-secondary-300', {
                'text-primary-500 [&>svg]:fill-primary-500': user.isPinned,
              })}
            />
          )}
          <MoreUserActions user={user} />
        </div>
        <div className="flex flex-1 gap-4">
          <CreatorAvatar
            name={user.displayName || user.name}
            avatarSrc={
              user.avatarThumbs?.c50 ?? user.avatarThumbs?.c144 ?? user.avatar
            }
            size={100}
            className="shrink-0"
            fallbackClassName="text-[32px]"
          />
          {/* TODO: Online state is not part of users list response. (See DEV-5626) */}
          <p className="pt-6 text-sm">
            {user.lastSeen &&
              `Seen ${formatDistanceToNow(new Date(user.lastSeen), {
                addSuffix: true,
              })}`}
          </p>
        </div>
      </div>
      <div className="p-3 pt-2">
        <div className="pl-[116px]">
          <div className="flex max-w-full justify-between gap-3 overflow-hidden">
            <div className="space-y-0.5 overflow-hidden">
              <p className="overflow-hidden text-ellipsis whitespace-nowrap font-medium leading-[26px]">
                {unescapeHtmlText(user.displayName || user.name)}
              </p>
              <p className="text-secondary-300 overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-[22px]">
                @{user.username}
              </p>
            </div>
            {user.isVerified && (
              <VerifiedIcon className="text-secondary-300 shrink-0" />
            )}
          </div>
        </div>
        <div className="mt-6 space-y-4">
          <ScrollingUserLists user={user} />
          <IconButton
            label="Message"
            onClick={handleClickMessage}
            disabled={isChatOpening}
            icon={ChatIcon}
            className="bg-primary-100 dark:bg-secondary-700 w-max rounded-full px-2 py-1.5 text-sm font-medium leading-6"
            iconClassName="m-0.5"
          />
          <Button
            disabled={
              user.subscribePrice !== 0 ||
              followMutation.isLoading ||
              unfollowMutation.isLoading
            }
            onClick={toggleFollow}
            className={classNames('flex w-full justify-around rounded-full', {
              'bg-transparent text-primary-500 hover:text-primary-500 dark:hover:border-primary-500':
                user.subscribedBy,
            })}
            variant={user.subscribedBy ? 'outline' : 'default'}
          >
            <span>{user.subscribedBy ? 'Subscribed' : 'Subscribe'}</span>
            <span>
              {user.subscribePrice === 0
                ? 'For Free'
                : user.subscribePrice + '$'}
            </span>
          </Button>
        </div>
      </div>
    </div>
  )
}
